import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  OnDestroy,
  AfterViewInit,
  inject,
} from '@angular/core';
import { HardwareVariant } from '../../../models/mobilephone-variant';
import { HardwareInsuranceOption } from '../../../models/mobilephone-search-item';
import { IconNames } from '@shared/enums/icon-names';
import { Subscription } from 'rxjs';
import { MobilephoneSearchService } from '../../../services/mobilephone-search.service';
import { NotificationService } from '@core/services/notification.service';
import { MobilePhoneProduct } from '@sales/models/mobilephone-product';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomerProductSelectors } from '@sales/states/customer-product/customer-product.selectors';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { MobilePaymentType } from '@sales/enums/mobile-payment-type';
import { DealerStateSelectors } from '@core/states/dealer/dealer-state.selectors';
import { select, Store } from '@ngxs/store';
import {
  ApiGetAllSubscriptionOfferingsAction,
  ApiGetSubscriberForVoiceSubscriptionAction,
} from '@sales/states/customer-product/customer-product.actions';
import { Regex } from '@shared/enums/regex';
import {
  DuplicateImeiInShoppingCartValidator,
  ImeiValidator,
  PreviouslyUsedImeiValidator,
} from '@sales/validators/imei-validator';
import { CustomerSelectors } from '@sales/modules/details/states/customer.selectors';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { ApiGetStockAction } from '@sales/states/warehouse/warehouse.actions';
import { WarehouseStateSelectors } from '@sales/states/warehouse/warehouse.selectors';
import { GtinValidator } from '@sales/validators/gtin-validator';
import { MobilephoneSearchHelperService } from '../../../services/mobilephone-search-helper.service';
import { EquipmentEventService } from '@app/sales/modules/equipment/equipment-event.service';
import { EquipmentVariant } from '@app/sales/modules/equipment/equipment.models';
import { ShoppingCartSelectors } from '@app/sales/states/shopping-cart/shopping-cart.selectors';
import { OrderService } from '@app/sales/services/order.service';
import { CustomerProductMobilePhoneSelectors } from '@app/sales/states/customer-product-mobilephone/customer-product-mobilephone.selectors';
import { SetIsValidatingImeiAction } from '@app/sales/states/customer-product-mobilephone/customer-product-mobilephone.actions';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import {
  InsuranceOptionNames,
  MobilephoneInsuranceOptionsComponent,
} from './mobilephone-insurance-options/mobilephone-insurance-options.component';
import { MobilephoneDeliveryTypeComponent } from './mobilephone-delivery-type/mobilephone-delivery-type.component';
import { EquipmentVoiceNumberTypeComponent } from '../../../../equipment/equipment-dialog/equipment-forms/equipment-voice-number-type/equipment-voice-number-type.component';
import { MobilephonePaymentDetailsComponent } from './mobilephone-payment-details/mobilephone-payment-details.component';
import { EquipmentIdentificationComponent } from '../../../../equipment/equipment-dialog/equipment-forms/equipment-identification/equipment-identification.component';
import { MobilephoneVariantOptionsComponent } from './mobilephone-variant-options/mobilephone-variant-options.component';
import { IceCardComponent } from '@app/shared/components/atoms/ice-card/ice-card.component';
import { MobilephoneInfoComponent } from './mobilephone-info/mobilephone-info.component';
import { ProductType } from '../../../models/mobilephone-search-enums';

enum Messages {
  ErrorGettingMobileDetails = 'Feil ved henting av mobildetaljer',
  CouldNotFindPhone = 'Fant ingen mobiler med scannet EAN/GTIN',
}

export class MobilePhoneDetails {
  color: string;
  memory: string;
  paymentType: MobilePaymentType;
  newOfferingId: string;
  existingSubscriptionId: string;
  isSubscriptionUpgrade: boolean;
  subscriptionIdToUpgrade: string;
  isSoldInShop: boolean;
  imei: string;
  isMissingIdentifiers: boolean;
  insuranceOption: string;
  gtin: string;
  name?: string;
  mobilBytteOffering: string;
}

export enum MobilePhoneFormControls {
  Color = 'color',
  Memory = 'memory',
  PaymentType = 'paymentType',
  NewOfferingId = 'newOfferingId',
  ExistingSubscriptionId = 'existingSubscriptionId',
  IsSubscriptionUpgrade = 'isSubscriptionUpgrade',
  SubscriptionIdToUpgrade = 'subscriptionIdToUpgrade',
  IsSoldInShop = 'isSoldInShop',
  Imei = 'imei',
  IsMissingIdentifiers = 'isMissingIdentifiers',
  InsuranceOption = 'insuranceOption',
  Gtin = 'gtin',
  ProductName = 'productName',
  MobilBytteOffering = 'mobilBytteOffering',
}

@Component({
  selector: 'app-mobilephone-details',
  templateUrl: './mobilephone-details.component.html',
  styleUrls: ['./mobilephone-details.component.scss'],
  imports: [
    MobilephoneInfoComponent,
    IceCardComponent,
    FormsModule,
    ReactiveFormsModule,
    MobilephoneVariantOptionsComponent,
    EquipmentIdentificationComponent,
    MobilephonePaymentDetailsComponent,
    EquipmentVoiceNumberTypeComponent,
    MobilephoneDeliveryTypeComponent,
    MobilephoneInsuranceOptionsComponent,
    IceButtonComponent,
  ],
})
export class MobilephoneDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private mobilePhoneSearchService = inject(MobilephoneSearchService);
  private mobilePhoneSearchHelperService = inject(MobilephoneSearchHelperService);
  private notificationService = inject(NotificationService);
  private equipmentEventService = inject(EquipmentEventService);
  private store = inject(Store);
  private formBuilder = inject(FormBuilder);
  private orderService = inject(OrderService);

  PriceType = PriceType;
  IconNames = IconNames;
  MobilePhoneFormControls = MobilePhoneFormControls;
  EquipmentVariant = EquipmentVariant;

  @Input() groupVariants: HardwareVariant[];
  @Input() mobilePhoneVariant: HardwareVariant;
  @Input() mobileProductInCart: MobilePhoneProduct;
  @Input() mobileProducts: MobilePhoneProduct[];
  @Input() allVoiceProducts: VoiceProduct[];
  @Input() isManualRegistration: boolean;
  @Input() canSellHardwareWithLogistics: boolean;
  @Input() canSellCellPhoneWithSubscription: boolean;
  @Input() canSellCellPhoneWithoutSubscription: boolean;
  @Output() backToSearch = new EventEmitter();
  @Output() addMobilePhoneProduct = new EventEmitter();

  warehouseState = select(WarehouseStateSelectors.getState);
  existingVoiceProducts = select(CustomerProductSelectors.getExistingVoiceProducts);
  isEquipmentUpgradeEnabled = select(DealerStateSelectors.isEquipmentUpgradeEnabled);
  hasPerformedDetailedCreditCheck = select(CustomerSelectors.hasPerformedDetailedCreditCheck);
  isEquipmentUpgradePreCheckSuccessful = select(CustomerSelectors.isEquipmentUpgradePreCheckSuccessful);
  existingProductsAvailableForEquipmentUpgrade = select(
    CustomerProductSelectors.getExistingVoiceSubscriptionsAvailableForEquipmentUpgrade
  );
  existingProductsAvailableForEquipmentUpgradeUpgrade = select(
    CustomerProductSelectors.getExistingVoiceSubscriptionsAvailableForEquipmentUpgradeUpgrade
  );
  isSubscriptionLimitReached = select(CustomerProductSelectors.isSubscriptionLimitReached);
  canSellMobileInsurance = select(DealerStateSelectors.canSellMobileInsurance);
  canOnlySellMobilePhoneWithSubscription = select(DealerStateSelectors.canOnlySellMobilePhoneWithSubscription);
  canUpdateImei = select(DealerStateSelectors.canUpdateImei);
  imeisInShoppingCart = select(ShoppingCartSelectors.imeisInShoppingCart);
  isValidatingImei = select(CustomerProductMobilePhoneSelectors.isValidatingImei);

  form: FormGroup = new FormGroup({});
  selectedVariant: HardwareVariant = null;
  subscriptions$: Subscription[] = [];
  mobileVariantFromShoppingCart: HardwareVariant;
  originalFormValues: MobilePhoneDetails;

  isFormUnchanged: boolean = true;
  isLoadingMobilePhoneOffering: boolean;
  hasMobilBytteProductInCart: boolean;
  isEquipmentUpgradeUpgrade: boolean;
  isSubmitted: boolean = false;

  defaultName = 'Manuell registrering';

  ngOnInit() {
    this.isEquipmentUpgradeUpgrade = !!this.mobileProductInCart?.existingAgreementId;

    this.subscriptions$.push(
      this.equipmentEventService.hardwareDetails.subscribe(response => {
        // Ignore the first default value from equipmentEventService
        if (response === undefined) {
          return;
        }

        if (!response) {
          this.setDefaultInsuranceOption();
          this.defaultName = 'Ukjent produkt';
        }

        if (this.mobilePhoneVariant !== response) {
          if (response?.productType !== ProductType.Cellphone) {
            this.form.controls.gtin.setErrors({ invalidAccessoryProductType: true });
          }
          this.mobilePhoneVariant = response;
          this.changeDetectorRef.detectChanges();
        }
      })
    );

    this.mobileVariantFromShoppingCart = this.mobileProductInCart?.mobilePhoneVariant;
    if (this.mobileProductInCart) {
      this.hasMobilBytteProductInCart = this.mobileProducts.some(
        p =>
          !p.isExistingSubscription &&
          p.paymentType === MobilePaymentType.EquipmentUpgrade &&
          p.id !== this.mobileProductInCart.id
      );
    } else {
      this.hasMobilBytteProductInCart = this.mobileProducts.some(
        p => !p.isExistingSubscription && p.paymentType === MobilePaymentType.EquipmentUpgrade
      );
    }

    this.initializeForm();
    this.setSelectedVariant();
    this.getWareHouseStatus();
  }

  private getWareHouseStatus() {
    if (this.mobilePhoneVariant) {
      const offeringIds = this.groupVariants?.map(v => v.offeringId).flat();
      this.store.dispatch(new ApiGetStockAction(offeringIds));
    }
  }

  private setDefaultInsuranceOption() {
    if (this.canSellMobileInsurance) {
      this.form.get(MobilePhoneFormControls.InsuranceOption)?.setValue(InsuranceOptionNames.NoInsurance);
      this.form.get(MobilePhoneFormControls.InsuranceOption)?.updateValueAndValidity();
    }
  }

  private setSelectedVariant() {
    if (this.mobileVariantFromShoppingCart) {
      if (this.mobileVariantFromShoppingCart.offeringId && this.groupVariants?.length > 0) {
        this.selectedVariant = this.groupVariants?.find(v => v.offeringId === this.mobileVariantFromShoppingCart.offeringId);
      } else {
        this.selectedVariant = this.mobileVariantFromShoppingCart;
      }
    }
    if (!this.selectedVariant && this.mobilePhoneVariant) {
      this.selectedVariant = this.mobilePhoneSearchHelperService.getCheapestVariantAvailableForPurchaseFromGroupVariants(
        this.groupVariants
      );
    }
    if (this.selectedVariant) {
      this.getMobileDetails(this.selectedVariant);
    }
  }

  private initializeForm() {
    const {
      selectedPaymentType,
      selectedNewOfferingId,
      selectedExistingSubscriptionId,
      selectedIsSubscriptionUpgrade,
      selectedSubscriptionIdToUpgrade,
      selectedIsSoldInShop,
      selectedImei,
      selectedShouldUpdateIdentifiersLater,
      selectedName,
      selectedInsuranceOption,
      selectedGtin,
      selectedMobilbytteOffering,
    } = this.getSelectedValues();

    this.form.addControl(
      MobilePhoneFormControls.Color,
      this.formBuilder.control({ value: null, disabled: this.isManualRegistration }, [Validators.required])
    );
    this.form.addControl(
      MobilePhoneFormControls.Memory,
      this.formBuilder.control({ value: null, disabled: this.isManualRegistration }, [Validators.required])
    );
    this.form.addControl(
      MobilePhoneFormControls.PaymentType,
      this.formBuilder.control(selectedPaymentType, [Validators.required])
    );
    this.form.addControl(
      MobilePhoneFormControls.NewOfferingId,
      this.formBuilder.control(selectedNewOfferingId, [Validators.required])
    );
    this.form.addControl(
      MobilePhoneFormControls.ExistingSubscriptionId,
      this.formBuilder.control(selectedExistingSubscriptionId)
    );
    this.form.addControl(
      MobilePhoneFormControls.IsSubscriptionUpgrade,
      this.formBuilder.control(selectedIsSubscriptionUpgrade, [Validators.required])
    );
    this.form.addControl(
      MobilePhoneFormControls.SubscriptionIdToUpgrade,
      this.formBuilder.control(selectedSubscriptionIdToUpgrade, [Validators.required])
    );
    this.form.addControl(
      MobilePhoneFormControls.IsSoldInShop,
      this.formBuilder.control(selectedIsSoldInShop, [Validators.required])
    );

    this.form.addControl(
      MobilePhoneFormControls.Gtin,
      this.formBuilder.control(selectedGtin, this.isManualRegistration ? [Validators.required, GtinValidator()] : [])
    );
    this.form.addControl(
      MobilePhoneFormControls.Imei,
      this.formBuilder.control(selectedImei, [
        Validators.required,
        Validators.minLength(15),
        Validators.maxLength(15),
        Validators.pattern(Regex.IMEI),
        ImeiValidator(),
      ])
    );
    if (this.canUpdateImei()) {
      // We want to ignore imei duplication check for the relevant imei when editing an item in the shopping cart.
      const indexOfExistingImei = this.imeisInShoppingCart()?.findIndex(imei => imei === selectedImei);
      const otherImeisInShoppingCart = this.imeisInShoppingCart()?.filter(imei => imei !== selectedImei);

      this.form
        .get(MobilePhoneFormControls.Imei)
        .addValidators([
          DuplicateImeiInShoppingCartValidator(
            indexOfExistingImei === -1 ? this.imeisInShoppingCart() : otherImeisInShoppingCart
          ),
        ]);
    }

    if (this.isManualRegistration) {
      this.form.addControl(
        MobilePhoneFormControls.ProductName,
        this.formBuilder.control(
          { value: selectedName, disabled: !!this.mobileProductInCart?.mobilePhoneVariant?.offeringId },
          [Validators.required]
        )
      );

      this.form
        .get(MobilePhoneFormControls.Imei)
        .addAsyncValidators([PreviouslyUsedImeiValidator(this.orderService, this.store)]);

      if (this.canUpdateImei()) {
        this.form.addControl(
          MobilePhoneFormControls.IsMissingIdentifiers,
          this.formBuilder.control(selectedShouldUpdateIdentifiersLater)
        );
      }
    }

    this.form.addControl(MobilePhoneFormControls.MobilBytteOffering, this.formBuilder.control(selectedMobilbytteOffering));

    if (this.canSellMobileInsurance()) {
      this.form.addControl(
        MobilePhoneFormControls.InsuranceOption,
        this.formBuilder.control(selectedInsuranceOption?.offeringId || null, [Validators.required])
      );
      if (this.mobileProductInCart && !this.mobileProductInCart.mobilePhoneVariant) {
        this.setDefaultInsuranceOption();
      }
    }
    if (this.mobileProductInCart) {
      this.subscriptions$.push(
        this.form.valueChanges.subscribe((values: MobilePhoneDetails) => {
          if (this.originalFormValues && values) {
            this.isFormUnchanged =
              this.mobileProductInCart.mobilePhoneVariant?.offeringId === this.selectedVariant?.offeringId &&
              this.formValuesAreSame(this.originalFormValues, values);
          }
        })
      );
    }
  }

  private getSelectedValues(): {
    selectedPaymentType: MobilePaymentType;
    selectedNewOfferingId: string;
    selectedExistingSubscriptionId: string;
    selectedIsSubscriptionUpgrade: boolean;
    selectedSubscriptionIdToUpgrade: string;
    selectedIsSoldInShop: boolean;
    selectedImei: string;
    selectedShouldUpdateIdentifiersLater: boolean;
    selectedName: string;
    selectedInsuranceOption: HardwareInsuranceOption;
    selectedGtin: string;
    selectedMobilbytteOffering: string;
  } {
    let selectedPaymentType: MobilePaymentType = null;
    let selectedNewOfferingId: string = null;
    let selectedExistingSubscriptionId: string = null;
    let selectedIsSubscriptionUpgrade: boolean = null;
    let selectedIsSoldInShop: boolean = null;
    let selectedImei: string = null;
    let selectedShouldUpdateIdentifiersLater: boolean = null;
    let selectedName: string = null;
    let selectedSubscriptionIdToUpgrade: string = null;
    let selectedInsuranceOption: HardwareInsuranceOption = null;
    let selectedGtin: string = null;
    let selectedMobilbytteOffering: string = null;
    if (this.mobileProductInCart) {
      selectedPaymentType = this.mobileProductInCart.paymentType;
      if (this.mobileProductInCart.voiceProductId) {
        selectedNewOfferingId = this.allVoiceProducts.find(p => p.id === this.mobileProductInCart.voiceProductId)?.offering
          ?.id;
        if (!selectedNewOfferingId) {
          selectedExistingSubscriptionId = this.allVoiceProducts.find(
            p => p.id === this.mobileProductInCart.voiceProductId && p.isExistingSubscription
          )?.subscription?.id;
        }
        selectedIsSubscriptionUpgrade = !!this.allVoiceProducts.find(
          p => p.id === this.mobileProductInCart.voiceProductId && p.isExistingSubscription
        )?.offering?.id;
        if (selectedIsSubscriptionUpgrade) {
          selectedSubscriptionIdToUpgrade = this.mobileProductInCart.voiceProductId;
        }
      }
      selectedIsSoldInShop = !this.mobileProductInCart.sendInPost;
      selectedInsuranceOption = this.mobileProductInCart.insuranceOption;
      selectedMobilbytteOffering = this.mobileProductInCart.mobilBytteOffering;

      if (this.mobileProductInCart.isManualRegistration) {
        selectedImei = this.mobileProductInCart.imei;
        selectedShouldUpdateIdentifiersLater = this.mobileProductInCart.isMissingIdentifiers;
        selectedName = this.mobileProductInCart.name;
        selectedGtin = this.mobileProductInCart.gtin;
      }
    }

    if (this.isManualRegistration || !this.canSellHardwareWithLogistics) {
      selectedIsSoldInShop = true;
    }
    return {
      selectedPaymentType,
      selectedNewOfferingId,
      selectedExistingSubscriptionId,
      selectedIsSubscriptionUpgrade,
      selectedSubscriptionIdToUpgrade,
      selectedIsSoldInShop,
      selectedImei,
      selectedShouldUpdateIdentifiersLater,
      selectedName,
      selectedInsuranceOption,
      selectedGtin,
      selectedMobilbytteOffering,
    };
  }

  ngAfterViewInit() {
    this.originalFormValues = this.form.value;
  }

  formValuesAreSame(originalValues: MobilePhoneDetails, newValues: MobilePhoneDetails): boolean {
    /// Using == instead of === on purpose, in order to ignore undefined/null difference
    return (
      originalValues.color === newValues.color &&
      originalValues.memory === newValues.memory &&
      originalValues.paymentType === newValues.paymentType &&
      /* tslint:disable: triple-equals */
      originalValues.newOfferingId == newValues.newOfferingId &&
      originalValues.existingSubscriptionId == newValues.existingSubscriptionId &&
      !originalValues.isSubscriptionUpgrade == !newValues.isSubscriptionUpgrade &&
      originalValues.subscriptionIdToUpgrade == newValues.subscriptionIdToUpgrade &&
      !originalValues.isSoldInShop == !newValues.isSoldInShop &&
      originalValues.imei == newValues.imei &&
      originalValues.isMissingIdentifiers == newValues.isMissingIdentifiers &&
      originalValues.name == newValues.name &&
      originalValues.insuranceOption == newValues.insuranceOption &&
      originalValues.gtin == newValues.gtin

      /* tslint:disable: triple-equals */
    );
  }

  private getMobileDetails(selectedMobilePhone: HardwareVariant) {
    this.isLoadingMobilePhoneOffering = true;

    if (!selectedMobilePhone) {
      this.notificationService.error(Messages.ErrorGettingMobileDetails);
      this.isLoadingMobilePhoneOffering = false;
    } else {
      this.isLoadingMobilePhoneOffering = false;
      this.mobilePhoneVariant = selectedMobilePhone;
      this.changeDetectorRef.detectChanges();
    }
  }

  onColorVariantChanged(offering: HardwareVariant) {
    this.selectedVariant = offering;
    this.getMobileDetails(this.selectedVariant);

    this.form.get(MobilePhoneFormControls.PaymentType)?.setValue(null);
    this.form.get(MobilePhoneFormControls.ExistingSubscriptionId)?.setValue(null);
    this.form.get(MobilePhoneFormControls.ExistingSubscriptionId)?.disable();
    this.form.get(MobilePhoneFormControls.NewOfferingId)?.setValue(null);
    this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade)?.setValue(null);
    this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade)?.disable();
    this.form.get(MobilePhoneFormControls.SubscriptionIdToUpgrade)?.setValue(null);
    this.form.get(MobilePhoneFormControls.SubscriptionIdToUpgrade)?.disable();

    this.form.updateValueAndValidity();
  }

  onPaymentTypeChanged(paymentType: MobilePaymentType) {
    if (paymentType === MobilePaymentType.PayNowWithoutSubscription) {
      this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade)?.setValue(null);
      this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade)?.disable();
      this.form.get(MobilePhoneFormControls.SubscriptionIdToUpgrade)?.setValue(null);
      this.form.get(MobilePhoneFormControls.SubscriptionIdToUpgrade)?.disable();
      this.form.get(MobilePhoneFormControls.IsSoldInShop)?.setValue(this.canSellHardwareWithLogistics ? false : true);
    } else {
      this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade)?.enable();
      if (this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade).value) {
        this.form.get(MobilePhoneFormControls.SubscriptionIdToUpgrade)?.enable();
      }
    }
    this.refreshIdentifierControls();
    this.form.updateValueAndValidity();
  }

  onEquipmentUpgradeNewOfferingChanged(offeringId: string) {
    if (offeringId) {
      this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade)?.enable();
    } else {
      this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade)?.setValue(null);
      this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade)?.disable();
      this.form.get(MobilePhoneFormControls.SubscriptionIdToUpgrade)?.setValue(null);
      this.form.get(MobilePhoneFormControls.SubscriptionIdToUpgrade)?.disable();
    }
    this.refreshIdentifierControls();
    this.form.updateValueAndValidity();
  }

  private refreshIdentifierControls() {
    const gtinControl = this.form.get(MobilePhoneFormControls.Gtin);
    const imeiControl = this.form.get(MobilePhoneFormControls.Imei);

    gtinControl?.updateValueAndValidity({ onlySelf: true, emitEvent: true });

    // Re-apply the product type error if the scanned GTIN belongs to an accessory, not a cellphone.
    // This is necessary because updateValueAndValidity() clears manually set errors.
    if (this.mobilePhoneVariant && this.mobilePhoneVariant.productType !== ProductType.Cellphone) {
      gtinControl?.setErrors({ ...gtinControl?.errors, invalidAccessoryProductType: true });
    }

    imeiControl?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
  }

  onGetExistingVoiceProductsDetails() {
    this.store.dispatch(new ApiGetAllSubscriptionOfferingsAction());
  }

  onSubmit() {
    if (!this.form.valid) {
      return;
    }

    this.isSubmitted = true;
    let mobilePhoneDetails: MobilePhoneDetails = this.form.value;

    if (mobilePhoneDetails.gtin === null) {
      mobilePhoneDetails.gtin = this.mobilePhoneVariant?.gtin;
    }

    if (!this.mobilePhoneVariant?.name) {
      mobilePhoneDetails.name = this.form.controls.productName?.value;
    }

    if (mobilePhoneDetails.isSubscriptionUpgrade && !!mobilePhoneDetails.subscriptionIdToUpgrade) {
      const existingVoiceProduct = this.allVoiceProducts.find(
        p => p.subscription.id === mobilePhoneDetails.subscriptionIdToUpgrade
      );

      if (!existingVoiceProduct) {
        this.store.dispatch(new ApiGetSubscriberForVoiceSubscriptionAction(mobilePhoneDetails.subscriptionIdToUpgrade));
      }
    }
    this.addMobilePhoneProduct.emit({
      mobilePhoneOffering: this.mobilePhoneVariant,
      mobilePhoneDetails,
    });
  }

  ngOnDestroy() {
    this.store.dispatch(new SetIsValidatingImeiAction(false));
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
